import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import {
  getEmptyCart,
  ShoppingCart,
  ShoppingCartExtraInformation,
  ShoppingCartExtraInfoStore,
  ShoppingCartRegistration,
} from './shopping-cart.interfaces.js';
import {
  AppStateAction,
  appStateActionEffect,
  ExtendedAuthService,
} from '../../util/auth/auth.service.js';
import {
  catchError,
  defaultIfEmpty,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { CustomerService } from '../../api/customer/customer.service.js';
import { Child } from '../../api/child/child.model.js';
import { Course } from '../../api/course/course.model.js';
import { DiscountService } from '../../api/discount/discount.service.js';
import { Discount } from '../../api/discount/discount.model.js';
import { AppliedDiscountSubscription } from '../../api/discount/lib/discount.interfaces.js';
import {
  isDiscountCode,
  sortByDiscountAdvantage,
} from '../../api/discount/lib/discount.util.js';
import { PaymentType } from '../../api/payment/lib/payment.interfaces.js';
import { ChildService } from '../../api/child/child.service.js';
import { PaymentService } from '../../api/payment/payment.service.js';
import { toSignal } from '@angular/core/rxjs-interop';
import { Customer } from '../../api/customer/customer.model.js';
import { differenceInYears, getYear, isBefore, subMonths } from 'date-fns';
import { Payment } from '../../api/payment/payment.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Workshop } from '../../api/workshop/workshop.model';
import { canRegisterForCourse, isCourse } from '../../api/course/course.util';
import { canRegisterForWorkshop } from '../../api/workshop/workshop.util';
import { getRegistrationKey } from '../../api/registration/registration.util';
import { getScheduleInterval } from '../../api/_lib/schedule.js';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { toStatefulSignal } from '../../util/stateful.signal';

@Injectable({ providedIn: 'root' })
export class ShoppingCartService {
  private readonly STORE_KEY = 'skillz-cart';
  private readonly CHILDREN_KEY = 'skillz-cart-children';
  private readonly EXTRA_INFO_KEY = 'skillz-cart-extra-info';
  public readonly cart = signal<ShoppingCart>(this.deserialize());
  private readonly tempChildren = signal<Child[]>(
    JSON.parse(localStorage.getItem(this.CHILDREN_KEY) ?? '[]'),
  );
  public readonly extraInfo = signal<ShoppingCartExtraInfoStore>(
    JSON.parse(localStorage.getItem(this.EXTRA_INFO_KEY) ?? '{}'),
  );
  private readonly userChildren = signal<Child[]>([]);
  public readonly children = computed(() => [
    ...this.tempChildren(),
    ...this.userChildren(),
  ]);
  private readonly user = toSignal(this.auth.user$, { initialValue: null });

  public readonly checkoutInProgress = signal(false);
  private readonly snackBar = inject(MatSnackBar);

  private readonly route = inject(ActivatedRoute);
  private readonly ongoingPayment = toStatefulSignal<
    [paymentId: string | null],
    Payment | null
  >(
    (paymentId) => {
      if (paymentId) {
        // Block the user from checking out while we're fetching the existing
        // checkout information of the ongoing payment.
        this.checkoutInProgress.set(true);
        return this.paymentService.read(paymentId).pipe(
          // We don't care about errors, if there's any error we create a new checkout.
          catchError(() => of(null)),
          finalize(() => this.checkoutInProgress.set(false)),
        );
      } else {
        return of(null);
      }
    },
    {
      initialValue: null,
      state: [this.route.queryParams.pipe(map((params) => params['payment']))],
    },
  );
  private readonly ongoingPaymentUntil = toSignal(
    this.route.queryParams.pipe(
      map((params) => (params['until'] ? Number(params['until']) : null)),
    ),
    { initialValue: null },
  );

  constructor(
    private readonly auth: ExtendedAuthService,
    private readonly childService: ChildService,
    private readonly customerService: CustomerService,
    private readonly discountService: DiscountService,
    private readonly paymentService: PaymentService,
  ) {
    // Whenever the cart or tempChildren change, store them in localStorage
    // Run a change hook when the user makes changes to their cart.
    let skipInitChange = true;
    effect(
      () => {
        if (!skipInitChange) {
          this.cartChangedByUser();
        }
        skipInitChange = false;
        localStorage.setItem(this.STORE_KEY, JSON.stringify(this.cart()));
      },
      { allowSignalWrites: true },
    );
    effect(() =>
      localStorage.setItem(
        this.CHILDREN_KEY,
        JSON.stringify(this.tempChildren()),
      ),
    );
    effect(() =>
      localStorage.setItem(
        this.EXTRA_INFO_KEY,
        JSON.stringify(this.extraInfo()),
      ),
    );

    // When someone navigates back from a payment method, their payment id is in
    // the URL, and when they checkout again the previous one should be resumed.

    // When someone is redirected to login during checkout, this action resumes
    // the checkout when they're redirected back after login.
    appStateActionEffect(
      (action: AppStateAction) => {
        if (action.type === 'checkout') {
          this.checkout(action.data.paymentType).subscribe({
            error: () => {
              this.snackBar.open($localize`Checkout error`, $localize`Dismiss`);
            },
          });
        }
      },
      { allowSignalWrites: true },
    );

    // Fetch the user, its children, and their discount subscriptions.
    this.auth.user$
      .pipe(
        switchMap((user) =>
          user ? this.customerService.getChildren(user) : of([]),
        ),
      )
      .subscribe((children) => this.userChildren.set(children));
  }

  private deserialize() {
    const jsonCart: ShoppingCart = JSON.parse(
      localStorage.getItem(this.STORE_KEY) ?? JSON.stringify(getEmptyCart()),
    );
    jsonCart.registrations = jsonCart.registrations.filter(canRegister);
    return jsonCart;
  }

  addRegistration(resource: Course | Workshop, child: Child) {
    const extraInfo = this.extraInfo()[resource.id] ?? {
      infoForCamp: '',
      infoForAdmin: '',
    };
    const registration = makeRegistration(resource, child, extraInfo);
    this.cart.update((cart) => ({
      ...cart,
      registrations: [...cart.registrations, registration],
    }));
    this.applySubscriptions(registration);
  }

  updateRegistration(registration: ShoppingCartRegistration) {
    this.cart.update((cart) => ({
      ...cart,
      registrations: cart.registrations.map((r) =>
        isSameRegistration(r, registration) ? registration : r,
      ),
    }));
  }

  updateExtraInfoForResource(resource: Course | Workshop, extraInfo: any) {
    this.extraInfo.update((info) => ({
      ...info,
      [resource.id]: {
        ...{ infoForCamp: '', infoForAdmin: '' },
        ...info[resource.id],
        ...extraInfo,
      },
    }));
  }

  addErrorToRegistration(index: number, message: string) {
    this.cart.update((cart) => ({
      ...cart,
      registrations: cart.registrations.map((r, i) =>
        i === index ? { ...r, error: message } : r,
      ),
    }));
  }

  removeRegistration(resource: Course | Workshop, child: Child) {
    this.cart.update((cart) => ({
      ...cart,
      registrations: cart.registrations.filter(
        (r) => !isSameRegistration(r, makeRegistration(resource, child)),
      ),
    }));
  }

  removeOutdatedRegistrations() {
    this.cart.update((cart) => ({
      ...cart,
      registrations: this.availabeRegistrations,
    }));
  }

  get availabeRegistrations() {
    return this.cart().registrations.filter(canRegister);
  }

  isRegistered(resource: Course | Workshop, child: Child) {
    const toFind = makeRegistration(resource, child);
    return this.cart().registrations.some((r) => isSameRegistration(r, toFind));
  }

  isResourceInCart(resource: Course | Workshop) {
    return this.cart().registrations.some(
      (r) => r.course?.id === resource.id || r.workshop?.id === resource.id,
    );
  }

  emptyCart() {
    this.cart.set(getEmptyCart());
    this.extraInfo.set({});
  }

  addChild(child: Child, resource?: Course | Workshop): Observable<Child> {
    if (this.user()) {
      return this.childService
        .create(
          new Child({
            ...child,
            parents: [{ id: this.user()!.id } as Customer],
            emergencyInformation: [],
          }),
        )
        .pipe(
          tap((newChild) => {
            this.userChildren.update((children) => [...children, newChild]);
            if (
              resource &&
              !childCannotRegisterOnResource(newChild, resource)
            ) {
              this.addRegistration(resource, newChild);
            }
          }),
        );
    } else {
      return of(child).pipe(
        map((child) => {
          child.id = '_' + String(crypto.randomUUID());
          return child;
        }),
        tap((child) => {
          this.tempChildren.update((children) => [...children, child]);
          if (resource && !childCannotRegisterOnResource(child, resource)) {
            this.addRegistration(resource, child);
          }
        }),
      );
    }
  }

  updateChild(child: Child) {
    if (this.user() && !isTemporaryChild(child)) {
      return this.childService.update(child.id, child).pipe(
        tap((updatedChild) => {
          this.userChildren.update((children) =>
            children.map((child) =>
              child.id === updatedChild.id ? updatedChild : child,
            ),
          );
        }),
      );
    } else {
      return of(child).pipe(
        tap((child) => {
          this.tempChildren.update((children) =>
            children.map((data) => (data.id === child.id ? child : data)),
          );
          const registration = this.cart().registrations.find(
            (reg) => reg.child.id === child.id,
          );
          if (!registration) return;

          registration!.child = child;
          this.updateRegistration(registration);
        }),
      );
    }
  }

  removeTemporaryChild(child: Child) {
    this.tempChildren.update((children) =>
      children.filter((data) => data.id !== child.id),
    );

    this.cart.update((cart) => ({
      ...cart,
      registrations: cart.registrations.filter(
        (registration) => registration.child.id !== child.id,
      ),
    }));
  }

  applySubscriptions(registration: ShoppingCartRegistration) {
    this.discountService.checkSubscriptions(registration).subscribe((subs) => {
      const candidates =
        subs
          .filter((sub): sub is AppliedDiscountSubscription => sub.applicable)
          .map((sub) => new Discount(sub)) ?? [];
      if (!candidates.length) return;
      registration.appliedDiscount = candidates.sort(
        (a, b) =>
          b.calculateDiscount(
            (registration.course ?? registration.workshop).price,
          ) -
          a.calculateDiscount(
            (registration.course ?? registration.workshop).price,
          ),
      )[0];
      this.updateRegistration(registration);
    });
  }

  applyDiscount(code: string, resource?: Course | Workshop) {
    let registrations = this.cart().registrations;
    if (resource)
      // The discount should only be applied to registrations for the given resource.
      registrations = registrations.filter(
        (registration) =>
          resource.id === (registration.course ?? registration.workshop).id,
      );

    // Check for each registration if the discount applies to it
    forkJoin(
      registrations.map((registration) =>
        // Associate each registration to its `checkDiscount` result.
        this.discountService
          .retrieveCode(code, {
            customer: this.user(),
            course: registration.course,
            workshop: registration.workshop,
          })
          .pipe(
            map((discount) => [registration, discount] as const),
            catchError((err) => {
              // Unknown error, rethrow it.
              if (!(err instanceof HttpErrorResponse) || err.status !== 404) {
                throw err;
              }
              // When the backend responds with 404 the discount doesn't exist
              // or does not apply to this registration.
              return of([registration, null]);
            }),
          ),
      ),
    ).subscribe((discounts) => {
      // Filter out all null responses as "not applicable"
      const applicableDiscounts = discounts.filter(
        (arg): arg is [ShoppingCartRegistration, Discount] => arg[1] !== null,
      );
      // No hits? Do nothing.
      if (!applicableDiscounts.length) return;
      // The user only entered the discount code, so here we take the server's
      // full discount answer which has all the discount information we need.
      // They are all the same code, so the first one will do.
      const discount = applicableDiscounts[0][1];
      // Unpack the applicable registrations
      const applicableRegistrations = applicableDiscounts.map((v) => v[0]);

      // Now we need to update the shopping cart state. We are going to reapply
      // a discount, so we begin by unapplying it.
      unapplyDiscount(applicableRegistrations, discount);

      // Then find which registrations best to apply it to
      const applyTo =
        discount.remainingUses === null
          ? // remainingUses is null, unlimited discount, apply to all
            applicableRegistrations
          : // Find the best X `remainingUses` candidates to apply it to.
            sortByDiscountAdvantage(discount, applicableRegistrations).slice(
              0,
              discount.remainingUses,
            );

      // Nothing to apply to? Do nothing more.
      if (!applyTo.length) return;

      // For these X candidates, update the `appliedDiscount`.
      this.cart.update((cart) => {
        for (const registration of cart.registrations) {
          for (const r of applyTo) {
            if (isSameRegistration(r, registration)) {
              registration.appliedDiscount = discount;
              break;
            }
          }
        }
        // Use shallow copies for possible diff/change detection.
        return { ...cart, registrations: [...cart.registrations] };
      });
    });
  }

  checkout<T extends PaymentType>(
    paymentType: T,
  ): Observable<{ payment: Payment | null }> {
    if (this.user() == null) {
      return this.auth.loginWithRedirect().pipe(map(() => ({ payment: null })));
    } else if (!untracked(this.checkoutInProgress)) {
      this.checkoutInProgress.set(true);
      const ongoingPayment = untracked(this.ongoingPayment);
      const ongoingPaymentUntil = untracked(this.ongoingPaymentUntil);

      if (
        ongoingPayment &&
        ongoingPaymentUntil &&
        isBefore(new Date(), ongoingPaymentUntil)
      ) {
        return of({ payment: ongoingPayment });
      }
      return this.createChildren().pipe(
        switchMap(() =>
          this.paymentService.checkout<T>(
            paymentType,
            this.cart(),
            this.extraInfo(),
          ),
        ),
        finalize(() => this.checkoutInProgress.set(false)),
      );
    } else {
      return of({ payment: null });
    }
  }

  createChildren() {
    const tempChildren = this.tempChildren();
    return forkJoin(
      tempChildren.map((child) =>
        this.childService.create(
          // todo: make guests log in before this point.
          new Child({
            ...child,
            parents: [{ id: this.user()!.id } as Customer],
            id: undefined,
            emergencyInformation: [],
          }),
        ),
      ),
    ).pipe(
      defaultIfEmpty([]),
      tap((newChildren: Child[]) => {
        const map = new Map(tempChildren.map((c, i) => [c.id, newChildren[i]]));
        this.cart.update((cart) => {
          const registrations = cart.registrations.map((registration) => ({
            ...registration,
            child: map.get(registration.child.id) ?? registration.child,
          }));
          return { ...cart, registrations };
        });
        this.tempChildren.set([]);
        this.userChildren.update((children) => [...children, ...newChildren]);
      }),
    );
  }

  canCheckoutCart(maxDays?: number): boolean {
    return this.cart().registrations.every((registration) =>
      canRegister(registration, maxDays),
    );
  }

  private cartChangedByUser() {
    // Invalidate the current ongoing payment immediately
    this.ongoingPayment.next(null);
    // And strip the query parameters from the current history item
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('payment');
    currentUrl.searchParams.delete('until');
    history.replaceState(null, '', currentUrl.href);
  }
}

function isSameRegistration(
  a: ShoppingCartRegistration,
  b: ShoppingCartRegistration,
) {
  return (
    a.child.id === b.child.id &&
    a.course?.id === b.course?.id &&
    a.workshop?.id === b.workshop?.id
  );
}

function unapplyDiscount(
  registrations: ShoppingCartRegistration[],
  discount: Discount,
) {
  for (const registration of registrations) {
    if (
      isDiscountCode(registration.appliedDiscount) &&
      registration.appliedDiscount.code === discount.code
    ) {
      delete registration.appliedDiscount;
    }
  }
}

function makeRegistration(
  resource: Course | Workshop,
  child: Child,
  extraInfo?: ShoppingCartExtraInformation,
): ShoppingCartRegistration {
  return {
    [getRegistrationKey(resource)]: resource,
    child,
    extraInformation: extraInfo,
  } as unknown as ShoppingCartRegistration;
}

function canRegister(
  registration: ShoppingCartRegistration,
  maxDays?: number,
): boolean {
  return (
    (registration.course &&
      canRegisterForCourse(registration.course, maxDays)) ||
    (registration.workshop &&
      canRegisterForWorkshop(registration.workshop, maxDays)) ||
    false
  );
}

function isTemporaryChild(child: Child) {
  return child.id.startsWith('_');
}

export function childCannotRegisterOnResource(
  child: Child,
  resource: Course | Workshop,
) {
  if (!child.dateOfBirth)
    return $localize`Please enter your child's date of birth.`;

  const startDate = getScheduleInterval(
    isCourse(resource) ? resource.camp.schedule : resource.schedule,
  ).start;

  const resourceYear = getYear(startDate);
  const childBirthYear = getYear(child.dateOfBirth);

  const childAgeInYear = resourceYear - childBirthYear;

  // A child that is the maxAge, but just turned that age up to 3 months before the start, is still allowed to register.
  const childAgeAtDate = differenceInYears(
    subMonths(startDate, 3),
    child.dateOfBirth,
  );

  if (childAgeInYear < resource.minAge || childAgeAtDate > resource.maxAge)
    return $localize`Your child is not in the age range for this course.`;
  return false;
}

export function getCheckoutError(error?: any) {
  if (!error) return { message: $localize`Checkout error`, registrations: [] };
  console.error(error);
  return {
    registrations: error.map((e: any) => {
      let message = '';
      if (e.meta.length >= 1) {
        switch (e.meta[0].constraint) {
          case 'notEnoughSpace':
            message = $localize`There is not enough space for this course.`;
            break;
          default:
            $localize`Checkout error`;
            break;
        }
      }

      return {
        index: Number(e.source.split('/')[2]),
        message,
      };
    }),
    message: $localize`Checkout error`,
  };
}
